import { Alert } from 'antd';

import { InputSwitch } from '../Input';

/**
 * The card-definition sub-editor for a `user` element's two options
 * (docs/plans/element-sets-and-me.md, "User data type"; keepr-api
 * utils/userElements.js validateUserOptions):
 *
 *   allowMultiple   several accounts on one item (a list of ids, sorted)
 *   identity        the ACCOUNT LINK — each item of the card names one keepr
 *                   account, one item per account per collection. This is
 *                   what "me" resolves through: the signed-in person's own
 *                   record here, on the filter row, in quick add's
 *                   {{me.…}}, wherever their assignee chip becomes their
 *                   badge.
 *
 * The two refuse each other (a link names ONE person), and a card's
 * effective list — its own elements, its ancestors', its element sets' —
 * carries at most one link. Both rules are enforced here before the save
 * and by the API on it (400 `invalid_options { element, conflictsWith }`),
 * so a refusal that still reaches this row lands under the switch.
 *
 * `identityHolder` is the element (other than this one) that already
 * carries the link in the effective list, or null.
 */
export default function UserOptions({ index, element, onChange, saveError = null, identityHolder = null, basePath = null, inGroup = false }) {
	const options = element?.options || {};
	const prefix = `${basePath || `elements[${index}]`}.options`;
	const allowMultiple = options.allowMultiple === true;
	const identity = options.identity === true;
	const holderLabel = identityHolder ? (identityHolder.label?.singular || identityHolder.name) : null;
	const refusal = saveError?.code === 'invalid_options' && saveError.element && saveError.element === element?.name
		? saveError
		: null;

	const handleMultiple = (_name, next) => {
		onChange(`${prefix}.allowMultiple`, next ? true : undefined);
		// A link names one person: turning the list on turns the link off.
		if (next && identity) onChange(`${prefix}.identity`, undefined);
	};

	const handleIdentity = (_name, next) => {
		onChange(`${prefix}.identity`, next ? true : undefined);
	};

	return (
		<div className='user-options'>
			<div className='micro-label card-def-element__section'>User</div>
			<div className='card-def-element__toggles'>
				<InputSwitch
					elementName={`${prefix}.allowMultiple`}
					elementLabel='Allow multiple'
					description='Accept more than one person.'
					value={allowMultiple}
					onChange={handleMultiple}
					first
				/>
				{inGroup ? null : <InputSwitch
					elementName={`${prefix}.identity`}
					elementLabel='Account link'
					description={holderLabel
						? `"${holderLabel}" is already this card's account link (one per card).`
						: (allowMultiple
							? 'A link names one person; turn off Allow multiple first.'
							: 'Links each item to a keepr account (one item per account in this collection).')}
					value={identity}
					onChange={handleIdentity}
					disabled={allowMultiple || !!identityHolder}
				/>}
			</div>
			{refusal ? (
				<Alert
					type='error'
					showIcon
					className='user-options__refusal'
					message={refusal.message || 'The API refused this element\'s options.'}
					description={refusal.conflictsWith ? `The account link is already carried by "${refusal.conflictsWith}".` : null}
				/>
			) : null}
			{inGroup ? null : <div className='form-panel__hint'>
				With an account link, &ldquo;me&rdquo; means the signed-in person&rsquo;s own record here: the Me
				chip on the list, <code>{'{{me.…}}'}</code> in quick add, and a person&rsquo;s chip becomes their record.
			</div>}
		</div>
	);
}
